import { create } from 'zustand';
import { UserProfile as OriginalUserProfile } from '@/types';

const isBrowser = typeof window !== 'undefined';

type PreferenceMode = 'light' | 'dark' | 'system';
type UserStateUpdater = UserProfile | null | ((previous: UserProfile | null) => UserProfile | null);

const getStorageItem = (key: string) => {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStorageItem = (key: string, value: string) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
};

const removeStorageItem = (key: string) => {
  if (!isBrowser) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
  }
};

const applyDarkClass = (darkMode: boolean) => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', darkMode);
};

const applyTextSizeClass = (size: 'small' | 'medium' | 'large') => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.remove('text-size-small', 'text-size-medium', 'text-size-large');
  document.documentElement.classList.add(`text-size-${size}`);
};

const getSystemDarkMode = () => {
  if (!isBrowser || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const getInitialDarkMode = () => {
  const storedPreference = getStorageItem('themePreference');
  if (storedPreference === 'dark') return true;
  if (storedPreference === 'light') return false;
  if (storedPreference === 'system') return getSystemDarkMode();
  return true;
};

const getInitialTextSize = () => (getStorageItem('textSize') as 'small' | 'medium' | 'large') || 'medium';

export interface UserProfile extends Omit<OriginalUserProfile, 'defaultModelId'> {
  defaultModelApiName?: string;
}

interface AppState {
  user: UserProfile | null;
  setUser: (user: UserStateUpdater) => void;
  darkMode: boolean;
  toggleDarkMode: (preference?: PreferenceMode) => void;
  textSize: 'small' | 'medium' | 'large';
  setTextSize: (size: 'small' | 'medium' | 'large') => void;
  saveHistory: boolean;
  setSaveHistory: (save: boolean) => void;
  allowAnalytics: boolean;
  setAllowAnalytics: (allow: boolean) => void;
  ollamaBaseUrl: string | null;
  setOllamaBaseUrl: (url: string | null) => void;
  cohereKey: string | null;
  setCohereKey: (key: string | null) => void;
}

export const useAppStore = create<AppState>()((set, get) => ({
  user: null,
  setUser: (user) =>
    set((state) => ({
      user: typeof user === 'function' ? user(state.user) : user,
    })),
  darkMode: getInitialDarkMode(),
  toggleDarkMode: (preference) => {
    const currentDarkMode = get().darkMode;
    let newDarkMode: boolean;
    let newPreference: PreferenceMode;

    if (preference === 'system') {
      newDarkMode = getSystemDarkMode();
      newPreference = 'system';
    } else if (preference === 'dark') {
      newDarkMode = true;
      newPreference = 'dark';
    } else if (preference === 'light') {
      newDarkMode = false;
      newPreference = 'light';
    } else {
      newDarkMode = !currentDarkMode;
      newPreference = newDarkMode ? 'dark' : 'light';
    }

    set({ darkMode: newDarkMode });
    setStorageItem('themePreference', newPreference);
    applyDarkClass(newDarkMode);
  },
  textSize: getInitialTextSize(),
  setTextSize: (size) => {
    set({ textSize: size });
    setStorageItem('textSize', size);
    applyTextSizeClass(size);
  },
  saveHistory: getStorageItem('saveHistory') !== 'false',
  setSaveHistory: (save) => {
    set({ saveHistory: save });
    setStorageItem('saveHistory', save.toString());
  },
  allowAnalytics: getStorageItem('allowAnalytics') !== 'false',
  setAllowAnalytics: (allow) => {
    set({ allowAnalytics: allow });
    setStorageItem('allowAnalytics', allow.toString());
  },
  ollamaBaseUrl: getStorageItem('ollamaBaseUrl') || null,
  setOllamaBaseUrl: (url) => {
    set({ ollamaBaseUrl: url });
    setStorageItem('ollamaBaseUrl', url || '');
  },
  cohereKey: getStorageItem('cohere_api_key') || null,
  setCohereKey: (key) => {
    set({ cohereKey: key });
    if (key) {
      setStorageItem('cohere_api_key', key);
    } else {
      removeStorageItem('cohere_api_key');
    }
  },
}));

if (isBrowser) {
  applyDarkClass(getInitialDarkMode());
  applyTextSizeClass(getInitialTextSize());

  if (getStorageItem('themePreference') === 'system' && typeof window.matchMedia === 'function') {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      useAppStore.getState().toggleDarkMode('system');
    });
  }
}
